import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CompagniaFilter } from "@/components/CompagniaFilter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { HandCoins, RefreshCw, Info, Wallet, ArrowUpRight, Eye, FileDown } from "lucide-react";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}

function formatDateIT(iso) {
  if (!iso) return "—";
  try {
    // Se è solo la data (YYYY-MM-DD) → dd/MM/yyyy
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
      const [y, m, d] = iso.split("-");
      return `${d}/${m}/${y}`;
    }
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

const RANGE_OPTIONS = [
  { key: "oggi", label: "Oggi" },
  { key: "7d", label: "7 giorni" },
  { key: "sempre", label: "Sempre" },
];

export default function DaVersare() {
  const [range, setRange] = useState("oggi");
  const [compagnia, setCompagnia] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/versamenti/da-versare", { params: { range, ...(compagnia ? { compagnia_id: compagnia } : {}) } });
      setData(r.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [range, compagnia]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [detail, setDetail] = useState({ open: false, loading: false, data: null });
  const openDetail = useCallback(async (userId) => {
    setDetail({ open: true, loading: true, data: null });
    try {
      const r = await api.get(`/versamenti/da-versare/${userId}/entries?range=${range}${compagnia ? `&compagnia_id=${compagnia}` : ""}`);
      setDetail({ open: true, loading: false, data: r.data });
    } catch (err) {
      toast.error(formatApiError(err));
      setDetail({ open: false, loading: false, data: null });
    }
  }, [range, compagnia]);
  const closeDetail = () => setDetail((d) => ({ ...d, open: false }));

  const [exporting, setExporting] = useState(false);
  const [exportingUserId, setExportingUserId] = useState(null);
  const exportRowPdf = useCallback(async (row) => {
    if (!row?.user_id) return;
    setExportingUserId(row.user_id);
    try {
      const res = await api.get(
        `/versamenti/da-versare/${row.user_id}/entries/export.pdf?range=${range}${compagnia ? `&compagnia_id=${compagnia}` : ""}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const cognome = (row.cognome || "collab").toLowerCase().replace(/\s+/g, "_");
      a.download = `da_versare_${cognome}_${range}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExportingUserId(null);
    }
  }, [range, compagnia]);
  const exportDetailPdf = useCallback(async () => {
    const u = detail.data?.user;
    if (!u?.id) return;
    setExporting(true);
    try {
      const res = await api.get(
        `/versamenti/da-versare/${u.id}/entries/export.pdf?range=${range}${compagnia ? `&compagnia_id=${compagnia}` : ""}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const cognome = (u.cognome || "collab").toLowerCase().replace(/\s+/g, "_");
      a.download = `da_versare_${cognome}_${range}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setExporting(false);
    }
  }, [detail.data, range]);

  const rows = data?.rows || [];
  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => (b.in_cassa || 0) - (a.in_cassa || 0));
  }, [rows]);
  const totals = data?.totals || { in_cassa: 0, in_attesa_convalida: 0 };
  const nCollabConSaldo = sorted.filter((r) => r.in_cassa > 0).length;

  return (
    <div className="max-w-6xl" data-testid="da-versare-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Contabilità</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <HandCoins className="w-6 h-6 text-emerald-600" />
            Da versare
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Liquidità (contante + assegno + recuperi contante/assegno) che ogni collaboratore ha
            in cassa e deve ancora consegnarti fisicamente.
          </p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <CompagniaFilter value={compagnia} onChange={setCompagnia} testId="da-versare-compagnia" className="min-w-[180px]" />
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="range-toggle">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setRange(opt.key)}
                className={`px-3 py-1.5 text-sm rounded-md whitespace-nowrap ${
                  range === opt.key
                    ? "bg-slate-900 text-white font-semibold"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
                data-testid={`range-${opt.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            disabled={loading}
            data-testid="da-versare-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <Card className="p-4 border-emerald-200 bg-emerald-50/60">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-emerald-800 font-semibold">
            <Wallet className="w-4 h-4" /> Totale da consegnare
          </div>
          <div className="text-3xl font-bold text-emerald-900 mt-1" data-testid="tot-in-cassa">
            {formatEUR(totals.in_cassa)}
          </div>
          <div className="text-xs text-emerald-800/70 mt-0.5">
            su {nCollabConSaldo} collaborator{nCollabConSaldo === 1 ? "e" : "i"}
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-semibold">
            <ArrowUpRight className="w-4 h-4" /> In attesa di tua convalida
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-1" data-testid="tot-pending">
            {formatEUR(totals.in_attesa_convalida)}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Versamenti già consegnati ma non ancora confermati
          </div>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500 font-semibold">
            <Info className="w-4 h-4" /> Metodi considerati
          </div>
          <div className="text-sm text-slate-800 mt-1 leading-relaxed">
            Contante · Assegno · Recupero contante · Recupero assegno
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Bonifico, POS e Postale non contano (accreditati direttamente).
          </div>
        </Card>
      </div>

      {/* Tabella */}
      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-400">Caricamento…</div>
        ) : sorted.length === 0 ? (
          <div className="p-10 text-center text-slate-400">
            <div className="text-3xl mb-2">🕊️</div>
            Nessun collaboratore attivo trovato nel periodo selezionato.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[11px]">
                <tr>
                  <th className="text-left px-4 py-3">Collaboratore</th>
                  <th className="text-right px-4 py-3">Incassato liquido</th>
                  <th className="text-right px-4 py-3">In attesa convalida</th>
                  <th className="text-right px-4 py-3">In cassa (da versare)</th>
                  <th className="text-center px-4 py-3">Note</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sorted.map((r) => {
                  const contoSep = r.conto_separato && r.gestione_liquidita_attiva;
                  const cassa = r.in_cassa || 0;
                  return (
                    <tr key={r.user_id} className="hover:bg-slate-50" data-testid={`da-versare-row-${r.user_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-slate-900">
                          {r.nome} {r.cognome}
                        </div>
                        <div className="text-xs text-slate-500">@{r.username}</div>
                      </td>
                      <td className="px-4 py-3 text-right text-slate-700">{formatEUR(r.range_incassato)}</td>
                      <td className="px-4 py-3 text-right text-slate-700">
                        {r.in_attesa_convalida > 0 ? (
                          <span className="inline-flex items-center gap-1">
                            {formatEUR(r.in_attesa_convalida)}
                            {r.n_versamenti_pending > 0 ? (
                              <Badge variant="outline" className="ml-1 bg-amber-50 text-amber-800 border-amber-200 text-[10px]">
                                {r.n_versamenti_pending} vers.
                              </Badge>
                            ) : null}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {r.libero_non_ripartibile ? (
                          <span className="text-xs text-slate-500" data-testid={`da-versare-libero-nd-${r.user_id}`}>n/d per compagnia (importo libero)</span>
                        ) : (
                        <span className={`font-bold text-lg ${cassa > 0 ? "text-emerald-700" : "text-slate-400"}`}>
                          {formatEUR(cassa)}
                        </span>
                        )}
                        {r.versamento_mode === "polizze" && (r.titoli_in_cassa || 0) > 0 && (
                          <div className="text-[10px] text-emerald-700/70">
                            {r.titoli_in_cassa} titol{r.titoli_in_cassa === 1 ? "o" : "i"} da versare
                          </div>
                        )}
                        {r.versamento_mode === "importo_libero" && cassa > 0 && (
                          <div className="text-[10px] text-slate-400 italic">versamento libero</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {contoSep ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 text-[10px]">
                            Conto separato
                          </Badge>
                        ) : r.sheets_count === 0 ? (
                          <span className="text-xs text-slate-400">nessun foglio</span>
                        ) : null}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDetail(r.user_id)}
                            disabled={cassa === 0 && r.in_attesa_convalida === 0}
                            data-testid={`da-versare-detail-${r.user_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            Dettaglio
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => exportRowPdf(r)}
                            disabled={cassa === 0 || exportingUserId === r.user_id}
                            data-testid={`da-versare-export-${r.user_id}`}
                            title="Esporta PDF dettaglio da versare"
                          >
                            <FileDown className={`w-3.5 h-3.5 ${exportingUserId === r.user_id ? "animate-pulse" : ""}`} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-50/60 font-semibold">
                <tr>
                  <td className="px-4 py-3 text-slate-700">Totale</td>
                  <td className="px-4 py-3 text-right text-slate-700">
                    {formatEUR(sorted.reduce((s, r) => s + (r.range_incassato || 0), 0))}
                  </td>
                  <td className="px-4 py-3 text-right text-slate-700">{formatEUR(totals.in_attesa_convalida)}</td>
                  <td className="px-4 py-3 text-right text-emerald-700 text-lg">
                    {formatEUR(totals.in_cassa)}
                  </td>
                  <td />
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <div className="mt-3 text-xs text-slate-500">
        Nota: I collaboratori con <strong>conto separato attivo</strong> non versano fisicamente in agenzia, ma accreditano sul proprio C/C.
      </div>

      {/* Drawer dettaglio entries liquide non versate */}
      <Sheet open={detail.open} onOpenChange={(o) => (o ? null : closeDetail())}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto overflow-x-hidden" data-testid="da-versare-drawer">
          <SheetHeader>
            <SheetTitle>
              Dettaglio da versare — {detail.data?.user?.nome} {detail.data?.user?.cognome}
            </SheetTitle>
            <SheetDescription>
              Entries liquide (contante · assegno · recupero contante · recupero assegno) non ancora
              versate. Periodo: <strong>{RANGE_OPTIONS.find((o) => o.key === range)?.label}</strong>.
            </SheetDescription>
          </SheetHeader>

          {detail.loading ? (
            <div className="p-8 text-center text-slate-400">Caricamento…</div>
          ) : (detail.data?.entries || []).length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              Nessuna entry liquida da versare in questo periodo.
            </div>
          ) : (
            <>
              <div className="mt-4 mb-2 flex items-center justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportDetailPdf}
                  disabled={exporting}
                  data-testid="drawer-export-pdf"
                >
                  <FileDown className={`w-4 h-4 mr-1.5 ${exporting ? "animate-pulse" : ""}`} />
                  {exporting ? "Genero PDF…" : "Esporta PDF"}
                </Button>
              </div>
              <div className="mb-3 rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                <div className="text-xs uppercase tracking-wide text-emerald-800 font-semibold">Totale liquido</div>
                <div className="text-2xl font-bold text-emerald-900" data-testid="drawer-total">
                  {formatEUR(detail.data?.total)}
                </div>
                <div className="text-xs text-emerald-800/70">
                  {detail.data.entries.length} {detail.data.entries.length === 1 ? "entry" : "entries"}
                </div>
              </div>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[10px]">
                    <tr>
                      <th className="text-left px-3 py-2 whitespace-nowrap">Data foglio</th>
                      <th className="text-left px-3 py-2">Contraente</th>
                      <th className="text-left px-3 py-2 whitespace-nowrap">N. Polizza</th>
                      <th className="text-left px-3 py-2 whitespace-nowrap">Metodo</th>
                      <th className="text-right px-3 py-2">Importo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detail.data.entries.map((e) => (
                      <tr key={e.id} className="hover:bg-slate-50" data-testid={`drawer-entry-${e.id}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-slate-700">
                          {formatDateIT(e.data_foglio_cassa)}
                        </td>
                        <td className="px-3 py-2 text-slate-900 font-medium">{e.contraente}</td>
                        <td className="px-3 py-2 text-slate-600">{e.numero_polizza || "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <Badge variant="outline" className={
                            e.metodo_pagamento.startsWith("Recupero")
                              ? "bg-blue-50 text-blue-800 border-blue-200 text-[10px]"
                              : "bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]"
                          }>
                            {e.metodo_pagamento}
                          </Badge>
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">
                          {formatEUR(e.netto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
